import {
  CallsProps,
  MostBusyData,
  SentimentsData,
} from '@dataTypes/message.types'
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  ChangeEvent,
} from 'react'
import moment from 'moment'
import { mockAppointments } from '@assets/mockdata/Appointments/appointments'
import { Appointment } from '@dataTypes/appointment.type'
import { SelectEventArgs } from '@syncfusion/ej2-react-dropdowns'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  createConversation,
  deleteCall,
  fetchAllConversations,
  generatePresignedUrlsForCalls,
  updateCallsById,
  updateSelectedMessage,
} from '@store/actions'
import {
  SearchSettingsModel,
  PageEventArgs,
  GridComponent,
} from '@syncfusion/ej2-react-grids'
import {
  ICallCount,
  ICallMetrics,
  ICalls,
  ICallTag,
} from '@dataTypes/calls.type'
import { getFullName } from '@utils/fullname.util'
import { Grid } from '@syncfusion/ej2-grids'
import { IContact } from '@dataTypes/contact.type'
import { ChatChannel } from '@dataTypes/chat.type'
import { MenuItemModel } from '@syncfusion/ej2-react-navigations'
import { NoteContextType } from '@dataTypes/note.type'
import {
  formatCallDate,
  getCallDisplayType,
  getCallAnswerState,
} from '@utils/call.util'
import {
  needsRecordingCanonicalize,
  recordingBlobNameFromRef,
  triggerRecordingDownload,
} from '@utils/call-recording-playback.util'
import { streamCallRecording } from '@services/api/calls.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import {
  SET_ACTIVE_TAB,
  SET_CONTACT,
  SET_INPUT_NUMBER,
  SET_PATIENT,
  SET_SHOW_MODAL,
} from '@store/reducers/messageCenter.reducer'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import type { NotesLocationState } from '@components/common/NoteEditorOverlay/notesFeature.types'
import type { Patient } from '@dataTypes/PatientInfo.types'
import {
  setDurationIndex,
  setFilters,
  setSearchQuery,
} from '@store/reducers/calls.reducer'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { CALLS_GRID_PAGE_SIZE } from '@constants/Message'
import { logger } from '@utils/safeLogger'
import { useAiReceptionistActorId } from '@hooks/useAiReceptionistActorId'
import { isAiAssistedCall } from '@utils/aiReceptionist.util'

export type TMenuAnchor = {
  id: string
  call: ICalls
  menuItems: MenuItemModel[]
  rect: DOMRect
} | null

const getGridRowIfBound = (
  grid: Grid,
  pageRowIndex: number,
): HTMLElement | null => {
  const row = grid.getRowByIndex(pageRowIndex) as HTMLElement | undefined
  if (!row) return null
  const uid = row.getAttribute('data-uid')
  if (!uid) return null
  try {
    const rowObject = grid.getRowObjectFromUID(uid) as
      | { data?: unknown }
      | undefined
    if (!rowObject?.data) return null
  } catch {
    return null
  }
  return row
}

const safeDetailExpand = (
  grid: Grid,
  detailToggleCell: HTMLElement,
): boolean => {
  try {
    grid.detailRowModule.expand(detailToggleCell)
    return true
  } catch {
    return false
  }
}

const safeDetailCollapse = (
  grid: Grid,
  detailToggleCell: HTMLElement,
): void => {
  try {
    grid.detailRowModule.collapse(detailToggleCell)
  } catch {
    console.error(
      'Failed to collapse detail row. This may be due to a grid refresh.',
    )
  }
}

const collapseGridDetailRow = (grid: Grid, index: number | null): void => {
  if (index == null) return
  const row = grid.getRowByIndex(index) as HTMLElement | undefined
  if (!row) return
  const icon = row.querySelector('.expand-btn')
  const detailToggleCell = row.querySelector(
    '.e-detailrowexpand, .e-detailrowcollapse',
  ) as HTMLElement | null
  if (detailToggleCell) {
    safeDetailCollapse(grid, detailToggleCell)
  }
  if (icon) {
    icon.classList.remove('mgc_down_small_fill')
    icon.classList.add('mgc_right_small_fill')
  }
  row.classList.remove('e-row-expanded')
}

const defaultCallMetrics: ICallMetrics = {
  total: {
    title: 'Total calls',
    description: 'Total number of incoming and outgoing calls',
    value: 0,
    percentage: 0,
  },
  answered: {
    title: 'Answered calls',
    description: 'Calls successfully answered by the patient or clinic',
    value: 0,
    percentage: 0,
  },
  missed: {
    title: 'Missed calls',
    description: 'Calls that were not answered by the clinic',
    value: 0,
    percentage: 0,
  },
  decline: {
    title: 'Declined calls',
    description: 'Calls declined by the patient',
    value: 0,
    percentage: 0,
  },
  followup: {
    title: 'Follow-ups',
    description: 'Calls that require follow-up',
    value: 0,
    percentage: 0,
  },
  handlingTime: {
    title: 'Avg. handling time',
    description:
      'Average time spent handling a call, including talk and wrap-up time',
    value: 0,
    percentage: 0,
  },
  waitTime: {
    title: 'Avg. wait time',
    description: 'Average time callers wait before their call is answered',
    value: 0,
    percentage: 0,
  },
  csat: {
    title: 'CSAT',
    description:
      'Customer Satisfaction Score estimated using AI sentiment analysis of calls ',
    value: 0,
    percentage: 0,
    classname: 'csat-card',
  },
}

type UseCallsProps = {
  onOpenNotesOverlay?: (
    state: NotesLocationState,
    patient: Patient | null,
  ) => void
}

function useCalls({ onOpenNotesOverlay }: UseCallsProps = {}): CallsProps {
  const dispatch = useAppDispatch()
  const clinicTimeZone = useAppSelector(
    (state) => state.clinic.selectedClinic?.time_zone || 'UTC',
  )
  const aiActorId = useAiReceptionistActorId()
  const { showToast } = useNotificationToast()
  const callsState = useAppSelector((state) => state.calls.data)
  const callsLoading = useAppSelector((state) => state.calls.loading)
  const filterIndex = useAppSelector((state) => state.calls.durationIndex)
  const { searchQuery, filters, durationIndex } = useAppSelector(
    (state) => state.calls,
  )

  const expandedRowIndexRef = useRef<number | null>(null)
  const expandedCallIdRef = useRef<string | null>(null)
  const pendingExpandCallIdRef = useRef<string | null>(null)
  const isDetailToggleInProgressRef = useRef(false)
  const gridRef = useRef<GridComponent>(null)

  const [appointmentData] = useState<Appointment[]>(mockAppointments)
  const [callCount, setCallCount] = useState<ICallCount>({
    totalCount: 0,
    totalPercent: 0,
    totalClassname: '',
    answeredCount: 0,
    answeredPercent: 0,
    answeredClassname: '',
    missedCount: 0,
    missedPercent: 0,
    missedClassname: '',
    declineCount: 0,
    declinePercent: 0,
    declineClassname: '',
    followupCount: 0,
    followupPercent: 0,
    followupClassname: '',
  })
  const [callMetrics, setCallMetrics] =
    useState<ICallMetrics>(defaultCallMetrics)
  const [averageDuration, setAverageDuration] = useState<number>(0)
  const [dayIndex, setDayIndex] = useState<number>(0)
  const defaultBusyDateCount: MostBusyData[] = useMemo(
    () => [
      { x: '6a', y: 0, color: 'var(--blue-500)' },
      { x: '7a', y: 0, color: 'var(--blue-500)' },
      { x: '8a', y: 0, color: 'var(--blue-500)' },
      { x: '9a', y: 0, color: 'var(--blue-500)' },
      { x: '10a', y: 0, color: 'var(--blue-500)' },
      { x: '11a', y: 0, color: 'var(--blue-500)' },
      { x: '12p', y: 0, color: 'var(--blue-500)' },
      { x: '1p', y: 0, color: 'var(--blue-500)' },
      { x: '2p', y: 0, color: 'var(--blue-500)' },
      { x: '3p', y: 0, color: 'var(--blue-500)' },
      { x: '4p', y: 0, color: 'var(--blue-500)' },
      { x: '5p', y: 0, color: 'var(--blue-500)' },
      { x: '6p', y: 0, color: 'var(--blue-500)' },
      { x: '7p', y: 0, color: 'var(--blue-500)' },
    ],
    [],
  )

  const [filteredAppointmentData, setFilteredAppointmentData] =
    useState<ICalls[][]>()
  const [filteredCallLogs, setFilteredCallLogs] = useState<ICalls[]>(callsState)
  const gap = 2
  const [mostBusyData, setMostBusyData] =
    useState<MostBusyData[]>(defaultBusyDateCount)
  const [mostBusyMaxCount, setMostBusyMaxCount] = useState<number>(0)
  const [peakHour, setPeakHour] = useState<string>('')
  const [sentimentDetails, setSentimentDetails] = useState<SentimentsData>({
    negative: { count: 0, percentage: 0 },
    neutral: { count: 0, percentage: 0 },
    positive: { count: 0, percentage: 0 },
    name: '',
    value: 0,
  })
  const [currentTime, setCurrentTime] = useState<string>('')
  const [tagVisibility, setTagVisibility] = useState<{
    [key: string]: boolean
  }>({})
  const [selectedCaller, setSelectedCaller] = useState<ICalls>()
  const [showCallerDetails, setShowCallerDetails] = useState<boolean>(false)

  useEffect(() => {
    if (!selectedCaller?.call_id) return
    const updated = callsState.find(
      (call) => call.call_id === selectedCaller.call_id,
    )
    if (!updated) return
    if (
      updated.summary !== selectedCaller.summary ||
      updated.transcript_text !== selectedCaller.transcript_text ||
      updated.sentiments !== selectedCaller.sentiments ||
      JSON.stringify(updated.tags) !== JSON.stringify(selectedCaller.tags)
    ) {
      setSelectedCaller(updated)
    }
  }, [callsState, selectedCaller])
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null)
  const conversations = useAppSelector((state) => state.chat.data)
  const [menuAnchor, setMenuAnchor] = useState<TMenuAnchor | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showAddContactModal, setShowAddContactModal] = useState<boolean>(false)
  const [showAISummaryModal, setShowAISummaryModal] = useState<boolean>(false)
  const [showAudioTranscriptModal, setShowAudioTranscriptModal] =
    useState<boolean>(false)
  const pageSize = CALLS_GRID_PAGE_SIZE
  const hasGeneratedFirstPageRef = useRef<boolean>(false)
  const requestedCallIdsRef = useRef<Set<string>>(new Set())

  const handlePageChange = useCallback(
    (args: PageEventArgs) => {
      if (!filteredCallLogs || filteredCallLogs.length === 0) return

      const currentPage =
        typeof args.currentPage === 'number'
          ? args.currentPage
          : parseInt(String(args.currentPage || 1), 10)
      const startIndex = (currentPage - 1) * pageSize
      const endIndex = startIndex + pageSize
      const currentPageCalls = filteredCallLogs.slice(startIndex, endIndex)

      // Canonicalize leftover filenames that are not already nested blob paths
      const callsNeedingUrls = currentPageCalls.filter(
        (call) =>
          call.call_id &&
          call.call_sid &&
          needsRecordingCanonicalize(call.recording_url) &&
          !requestedCallIdsRef.current.has(call.call_id),
      )

      if (callsNeedingUrls.length > 0) {
        // Mark these calls as requested
        callsNeedingUrls.forEach((call) => {
          if (call.call_id) {
            requestedCallIdsRef.current.add(call.call_id)
          }
        })
        dispatch(generatePresignedUrlsForCalls(callsNeedingUrls))
      }
    },
    [filteredCallLogs, dispatch, pageSize],
  )

  // Generate presigned URLs for first page when data loads (only once)
  useEffect(() => {
    if (
      filteredCallLogs &&
      filteredCallLogs.length > 0 &&
      !hasGeneratedFirstPageRef.current
    ) {
      const firstPageCalls = filteredCallLogs.slice(0, pageSize)
      const callsNeedingUrls = firstPageCalls.filter(
        (call) =>
          call.call_id &&
          call.call_sid &&
          needsRecordingCanonicalize(call.recording_url) &&
          !requestedCallIdsRef.current.has(call.call_id),
      )

      if (callsNeedingUrls.length > 0) {
        hasGeneratedFirstPageRef.current = true
        // Mark these calls as requested
        callsNeedingUrls.forEach((call) => {
          if (call.call_id) {
            requestedCallIdsRef.current.add(call.call_id)
          }
        })
        dispatch(generatePresignedUrlsForCalls(callsNeedingUrls))
      }
    }

    // Reset when filteredCallLogs changes significantly (e.g., new data loaded)
    if (filteredCallLogs && filteredCallLogs.length === 0) {
      hasGeneratedFirstPageRef.current = false
      requestedCallIdsRef.current.clear()
    }
  }, [filteredCallLogs, dispatch, pageSize])

  // Generate presigned URL for a single call (used when AudioTemplate detects missing URL)
  const generatePresignedUrlForCall = useCallback(
    (call: ICalls) => {
      if (
        call.call_id &&
        call.call_sid &&
        needsRecordingCanonicalize(call.recording_url) &&
        !requestedCallIdsRef.current.has(call.call_id)
      ) {
        requestedCallIdsRef.current.add(call.call_id)
        dispatch(generatePresignedUrlsForCalls([call]))
      }
    },
    [dispatch],
  )

  const actionItems: MenuItemModel[] = [
    {
      id: 'caller-details',
      text: 'Caller details',
      iconCss: 'mgc_user_info_fill',
    },
    {
      id: 'tag-as',
      text: 'Tag as',
      iconCss: 'mgc_tag_fill',
      items: [
        { id: 'Feedback', text: 'Feedback' },
        { id: 'Inquiry', text: 'Inquiry' },
        { id: 'Resolved', text: 'Resolved' },
        { id: 'Unresolved', text: 'Unresolved' },
        { id: 'Follow-up required', text: 'Follow-up required' },
        { id: 'High priority', text: 'High priority' },
      ],
    },
    {
      id: 'add-note',
      text: 'Add note',
      iconCss: 'mgc_document_fill',
    },
    {
      id: 'delete-call',
      text: 'Delete call log',
      iconCss: 'mgc_delete_2_fill',
    },
  ]

  const addContactItem: MenuItemModel = {
    id: 'add-contact',
    text: 'Add contact',
    iconCss: 'mgc_user_add_2_fill',
  }

  const setAppointmentItem: MenuItemModel = {
    id: 'set-appointment',
    text: 'Set appointment',
    iconCss: 'mgc_bell_ringing_fill',
  }

  const downloadRecordingItem: MenuItemModel = {
    id: 'download-recording',
    text: 'Download recording',
    iconCss: 'mgc_download_2_line',
  }

  const tagItems: string[] = [
    'High priority',
    'Follow-up required',
    'Unresolved',
    'Resolved',
    'Inquiry',
  ]

  const downloadAudio = async (audioSrc: string): Promise<void> => {
    if (!audioSrc) {
      console.error('No audio source provided')
      return
    }

    const callId = selectedCaller?.call_id ?? String(Date.now())
    const blobName = recordingBlobNameFromRef(audioSrc)
    let objectUrl = ''
    try {
      if (blobName) {
        objectUrl = await streamCallRecording(blobName)
        triggerRecordingDownload(objectUrl, callId)
        return
      }

      const response = await fetch(audioSrc, {
        mode: 'cors',
        credentials: 'omit',
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const buffer = await response.arrayBuffer()
      const audioBlob = new Blob([buffer], { type: 'audio/mpeg' })
      objectUrl = URL.createObjectURL(audioBlob)
      triggerRecordingDownload(objectUrl, callId)
    } catch (error) {
      logger.error('Error downloading audio:', error)
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }

  const handleOnClickDropDown = (
    props: ICalls,
    buttonRef: RefObject<HTMLButtonElement>,
    menuItems: MenuItemModel[],
  ): void => {
    setSelectedCaller(props)
    const rect = buttonRef.current!.getBoundingClientRect()
    setMenuAnchor({ id: props.call_id, call: props, rect, menuItems })
    if (props.contact) dispatch(SET_CONTACT(props.contact))
    if (props.contact?.patient) dispatch(SET_PATIENT(props.contact?.patient))
  }

  const buildNotesState = useCallback(
    (props: ICalls, openFirstNote: boolean) => {
      if (props.contact) dispatch(SET_CONTACT(props.contact))
      if (props.contact?.patient) dispatch(SET_PATIENT(props.contact.patient))
      const contextName =
        props.start_date_time != null
          ? `Call – ${formatCallDate(props.start_date_time, clinicTimeZone)}`
          : 'Call'
      return {
        patientId:
          props.contact?.patient?.patient_id ?? props.contact?.patient_id,
        contactId: props.contact_id,
        contextId: props.call_id,
        contextType: NoteContextType.CALL,
        contextName,
        previousPage: { path: '/message', tab: 'calls' as const },
        ...(openFirstNote && { openFirstNote: true }),
      }
    },
    [dispatch, clinicTimeZone],
  )

  /** Open Notes overlay to add a new note for this call (used from Actions dropdown "Add note"). */
  const handleAddNote = (props: ICalls): void => {
    setSelectedCaller(props)
    onOpenNotesOverlay?.(
      buildNotesState(props, false),
      (props.contact?.patient as unknown as Patient | undefined) ?? null,
    )
  }

  /** Open Notes overlay to view notes for this call (used from Notes column icon when call has notes). */
  const handleViewNotes = (props: ICalls): void => {
    setSelectedCaller(props)
    onOpenNotesOverlay?.(
      buildNotesState(props, true),
      (props.contact?.patient as unknown as Patient | undefined) ?? null,
    )
  }

  const handleSelect = async (
    args: { item: MenuItemModel },
    details: ICalls,
  ): Promise<void> => {
    setMenuAnchor(null)

    const selectedItem = args.item.id
    if (selectedItem && details.call_id) {
      switch (args.item.id) {
        case 'caller-details':
          setSelectedCaller(details)
          setShowCallerDetails(true)
          break
        case 'set-appointment':
          dispatch(SET_SHOW_MODAL({ appointment: true }))
          break
        case 'add-contact':
          setShowAddContactModal(true)
          break
        case 'download-recording':
          {
            downloadAudio(selectedCaller?.recording_url ?? '')
          }
          break
        case 'tag-as':
          setTagVisibility((prev) => ({
            ...prev,
            [details.call_id]: !prev[details.call_id],
          }))
          break
        case 'add-note':
          handleAddNote(details)
          break
        case 'delete-call':
          setShowDeleteModal(true)
          break
        default:
          {
            if (
              [
                'Feedback',
                'Inquiry',
                'Resolved',
                'Unresolved',
                'Follow-up required',
                'High priority',
              ].includes(selectedItem)
            ) {
              const existingTags = (details.tags ?? []) as ICallTag[]
              const isAlreadyTagged = existingTags.includes(
                selectedItem as ICallTag,
              )
              const updatedTags = isAlreadyTagged
                ? existingTags.filter((t) => t !== (selectedItem as ICallTag))
                : [...existingTags, selectedItem as ICallTag]
              try {
                await dispatch(
                  updateCallsById({
                    call_id: details.call_id,
                    updates: { tags: updatedTags },
                  }),
                ).unwrap()
                showToast(
                  createSuccessToast(
                    'Success',
                    isAlreadyTagged
                      ? 'Tag removed successfully.'
                      : 'Tag added successfully.',
                  ),
                )
              } catch (err) {
                const message =
                  typeof err === 'string'
                    ? err
                    : err && typeof err === 'object' && 'message' in err
                      ? String((err as { message: unknown }).message)
                      : 'Updating tag failed. Please try again.'
                showToast(createFailedToast('Error', message))
              }
            }
          }
          break
      }
    }
  }

  const handleSelectTag = (args: SelectEventArgs, details: ICalls): void => {
    setTagVisibility((prev) => ({
      ...prev,
      [details.call_id]: !prev[details.call_id],
    }))
  }

  const handleShowAISummaryModal = (props: ICalls): void => {
    setShowAISummaryModal(true)
    setSelectedCaller(props)
  }

  const searchOption: SearchSettingsModel = {
    fields: [
      'patient.first_name',
      'patient.middle_name',
      'patient.last_name',
      'patient.cellphone',
      'type',
      'start_date_time',
      'duration',
      'sentiments',
      'tags',
    ],
    key: '',
    operator: 'contains',
    ignoreCase: true,
  }

  const dayDropdownOptions = useMemo(
    () => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    [],
  )

  const callsDropdownOptions: string[] = ['week', 'month', 'year']

  const getPeakHourPerDay = useCallback((): void => {
    const dayData = mostBusyData
    let max = 0
    let maxKey = ''

    dayData.forEach((element) => {
      if (element.y > 0 && element.y > max) {
        max = element.y
        maxKey = element.x
      }
    })

    const timeIndicator = maxKey[maxKey.length - 1] + 'm'
    let timeDisplay = '-'
    if (!isNaN(parseInt(maxKey))) {
      let endTime = `${parseInt(maxKey) + 1} ${timeIndicator}`
      if (parseInt(maxKey) === 12)
        endTime = `1 ${timeIndicator === 'am' ? 'pm' : 'am'}`
      else if (parseInt(maxKey) === 11)
        endTime = `12 ${timeIndicator === 'am' ? 'pm' : 'am'}`
      timeDisplay = `${parseInt(maxKey)} ${timeIndicator} to ${endTime}`
    }
    setPeakHour(timeDisplay)
  }, [mostBusyData])

  const filterAppointmentByDay = useCallback(() => {
    const returnData = dayDropdownOptions.map((day) =>
      callsState?.filter(
        (log) => moment(log.start_date_time).format('dddd') === day,
      ),
    )

    const maxCount = Math.max(0, ...returnData.map((arr) => arr.length))
    return { returnData, maxCount }
  }, [callsState, dayDropdownOptions])

  const handleDayOptionChange = (day: string): void => {
    const index = dayDropdownOptions.findIndex((data) => data === day)
    setDayIndex(index)
  }

  const handleClosePatientInfoModal = (): void => {
    setSelectedCaller(undefined)
  }

  const handleSendMessage = (contact: IContact): void => {
    dispatch(SET_ACTIVE_TAB('chat'))

    // Find existing SMS conversation by phone number or contact_id
    const chat = conversations?.find((c) => {
      // Only check SMS conversations
      if (c.channel !== 'SMS' || c.is_hidden) {
        return false
      }

      // Only check conversations with a single participant
      if (!c.chat_participant || c.chat_participant.length !== 1) {
        return false
      }

      const participant = c.chat_participant[0]

      // Check#1: match by phone number
      const phoneMatch = participant.phone_number === contact.cellphone

      // Check#2: match by contact_id if available
      const contactIdMatch =
        contact.contact_id &&
        participant.contact_id &&
        participant.contact_id === contact.contact_id

      return phoneMatch || contactIdMatch
    })

    if (chat && chat.chat_conversation_id) {
      dispatch(updateSelectedMessage({ id: chat.chat_conversation_id }))
    } else {
      dispatch(
        createConversation({
          conversation_data: {
            channel: ChatChannel.sms,
            chat_message: [],
            chat_participant: [],
          },
          members_data: [
            {
              chat_conversation_id: '',
              phone_number: contact.cellphone,
              contact_id: contact.contact_id ?? undefined,
            },
          ],
        }),
      ).then(() => dispatch(fetchAllConversations()))
    }
  }

  const handleNextDay = (): void => {
    if (dayIndex === dayDropdownOptions.length - 1) {
      setDayIndex(0)
    } else {
      setDayIndex(dayIndex + 1)
    }
  }

  const handlePreviousDay = (): void => {
    if (dayIndex) {
      setDayIndex(dayIndex - 1)
    } else {
      setDayIndex(dayDropdownOptions.length - 1)
    }
  }

  const getCountClassname = (percentage: number): string => {
    if (percentage === 0) return 'neutral'
    else if (percentage > 0) return 'increase'
    else return 'decrease'
  }

  const getCallCounts = useCallback(() => {
    const now = moment()

    // Default filter is week
    let currentData: ICalls[] = callsState.filter((el) =>
      moment(el.start_date_time).isSame(now, 'week'),
    )
    let previousData: ICalls[] = callsState.filter((el) =>
      moment(el.start_date_time).isSame(
        now.clone().subtract(1, 'week'),
        'week',
      ),
    )

    if (filterIndex === 1) {
      // 1 is equivalent to filter by month
      currentData = callsState.filter((el) =>
        moment(el.start_date_time).isSame(now, 'month'),
      )
      previousData = callsState.filter((el) =>
        moment(el.start_date_time).isSame(
          now.clone().subtract(1, 'month'),
          'month',
        ),
      )
    } else if (filterIndex === 2) {
      // 2 is equivalent to filter by year
      currentData = callsState.filter((el) =>
        moment(el.start_date_time).isSame(now, 'year'),
      )
      previousData = callsState.filter((el) =>
        moment(el.start_date_time).isSame(
          now.clone().subtract(1, 'year'),
          'year',
        ),
      )
    }
    // For total calls
    const currentTotal = Number(currentData.length)
    const prevTotal = Number(previousData.length)
    const totalPercent =
      currentTotal > 0 && !isNaN(currentTotal) && !isNaN(prevTotal)
        ? Math.floor((currentTotal - prevTotal) / currentTotal)
        : 0

    // For answered calls
    const currentAnswer = Number(
      currentData.filter((el) => getCallAnswerState(el) === true).length,
    )
    const prevAnswer = Number(
      previousData.filter((el) => getCallAnswerState(el) === true).length,
    )
    const answerPercent =
      currentAnswer > 0 && !isNaN(currentAnswer) && !isNaN(prevAnswer)
        ? Math.floor((currentAnswer - prevAnswer) / currentAnswer)
        : 0

    // For missed calls
    const currentMissed = Number(
      currentData.filter((el) => getCallDisplayType(el) === 'Missed').length,
    )
    const prevMissed = Number(
      previousData.filter((el) => getCallDisplayType(el) === 'Missed').length,
    )
    const missedPercent =
      currentMissed > 0 && !isNaN(currentMissed) && !isNaN(prevMissed)
        ? Math.floor((currentMissed - prevMissed) / currentMissed)
        : 0

    // For declined calls
    const currentDecline = Number(
      currentData.filter((el) => getCallDisplayType(el) === 'Decline').length,
    )
    const prevDecline = Number(
      previousData.filter((el) => getCallDisplayType(el) === 'Decline').length,
    )
    const declinePercent =
      currentDecline > 0 && !isNaN(currentDecline) && !isNaN(prevDecline)
        ? Math.floor((currentDecline - prevDecline) / currentDecline)
        : 0

    // For calls that are tagged as follow-up required
    const currentFollowup = Number(
      currentData.filter((el) => (el.tags ?? []).includes('Follow-up required'))
        .length,
    )
    const prevFollowup = Number(
      previousData.filter((el) =>
        (el.tags ?? []).includes('Follow-up required'),
      ).length,
    )
    const followupPercent =
      currentFollowup > 0 && !isNaN(currentFollowup) && !isNaN(prevFollowup)
        ? Math.floor((currentFollowup - prevFollowup) / currentFollowup)
        : 0

    setCallCount({
      totalCount: currentTotal,
      totalPercent: Math.abs(totalPercent),
      totalClassname: getCountClassname(totalPercent),
      answeredCount: currentAnswer,
      answeredPercent: Math.abs(answerPercent),
      answeredClassname: getCountClassname(answerPercent),
      missedCount: currentMissed,
      missedPercent: Math.abs(missedPercent),
      missedClassname: getCountClassname(missedPercent),
      declineCount: currentDecline,
      declinePercent: Math.abs(declinePercent),
      declineClassname: getCountClassname(declinePercent),
      followupCount: currentFollowup,
      followupPercent: Math.abs(followupPercent),
      followupClassname: getCountClassname(followupPercent),
    })

    const currentWithSentiment = currentData.filter((el) =>
      ['Positive', 'Negative', 'Neutral'].includes(el.sentiments),
    )
    const currentSentimentTotal = currentWithSentiment.length

    const positive = currentWithSentiment.filter(
      (el) => el.sentiments === 'Positive',
    ).length
    const positivePercent =
      currentSentimentTotal > 0
        ? Math.floor((positive / currentSentimentTotal) * 100)
        : 0
    const negative = currentWithSentiment.filter(
      (el) => el.sentiments === 'Negative',
    ).length
    const negativePercent =
      currentSentimentTotal > 0
        ? Math.floor((negative / currentSentimentTotal) * 100)
        : 0
    const neutral = currentWithSentiment.filter(
      (el) => el.sentiments === 'Neutral',
    ).length
    const neutralPercent =
      currentSentimentTotal > 0
        ? Math.floor((neutral / currentSentimentTotal) * 100)
        : 0

    const prevWithSentiment = previousData.filter((el) =>
      ['Positive', 'Negative', 'Neutral'].includes(el.sentiments),
    )
    const prevSentimentTotal = prevWithSentiment.length
    const prevPositive = prevWithSentiment.filter(
      (el) => el.sentiments === 'Positive',
    ).length
    const prevPositivePercent =
      prevSentimentTotal > 0
        ? Math.floor((prevPositive / prevSentimentTotal) * 100)
        : 0
    const csatChangePercent =
      positivePercent > 0 && prevPositivePercent > 0
        ? positivePercent - prevPositivePercent
        : 0

    const partialSentiments = {
      positive: {
        count: positive,
        percentage: positivePercent,
      },
      neutral: {
        count: neutral,
        percentage: neutralPercent,
      },
      negative: {
        count: negative,
        percentage: negativePercent,
      },
    }

    const value = Math.max(positivePercent, neutralPercent, negativePercent)
    const index = [positivePercent, neutralPercent, negativePercent].findIndex(
      (el) => el === value,
    )
    const name = index === 0 ? 'Positive' : index === 1 ? 'Neutral' : 'Negative'

    setSentimentDetails({
      ...partialSentiments,
      name,
      value: isNaN(value) ? 0 : value,
    })
    // Average wait/handling time (exclude Declined and Missed calls)
    const completedCalls = currentData.filter(
      (item) => getCallAnswerState(item) === true,
    )
    const prevCompletedCalls = previousData.filter(
      (item) => getCallAnswerState(item) === true,
    )

    const getAverage = (
      items: ICalls[],
      key: 'waiting_time' | 'handling_time',
    ): number => {
      if (!items.length) return 0
      return Math.round(
        items.reduce((sum, item) => sum + (item[key] ?? 0), 0) / items.length,
      )
    }

    const currentAvgHandlingTime = getAverage(completedCalls, 'handling_time')
    const prevAvgHandlingTime = getAverage(prevCompletedCalls, 'handling_time')
    const currentAvgWaitTime = getAverage(completedCalls, 'waiting_time')
    const prevAvgWaitTime = getAverage(prevCompletedCalls, 'waiting_time')

    const handlingTimePercent =
      currentAvgHandlingTime > 0 && prevAvgHandlingTime > 0
        ? ((currentAvgHandlingTime - prevAvgHandlingTime) /
            prevAvgHandlingTime) *
          100
        : 0
    const waitTimePercent =
      currentAvgWaitTime > 0 && prevAvgWaitTime > 0
        ? ((currentAvgWaitTime - prevAvgWaitTime) / prevAvgWaitTime) * 100
        : 0

    setAverageDuration(currentAvgHandlingTime)

    setCallMetrics({
      ...callMetrics,
      total: {
        ...callMetrics.total,
        value: currentTotal,
        percentage: Math.round(totalPercent),
        classname: getCountClassname(totalPercent),
      },
      answered: {
        ...callMetrics.answered,
        value: currentAnswer,
        percentage: Math.round(answerPercent),
        classname: getCountClassname(answerPercent),
      },
      missed: {
        ...callMetrics.missed,
        value: currentMissed,
        percentage: Math.round(missedPercent),
        classname: getCountClassname(missedPercent),
      },
      decline: {
        ...callMetrics.decline,
        value: currentDecline,
        percentage: Math.round(declinePercent),
        classname: getCountClassname(declinePercent),
      },
      followup: {
        ...callMetrics.followup,
        value: currentFollowup,
        percentage: Math.round(followupPercent),
        classname: getCountClassname(followupPercent),
      },
      handlingTime: {
        ...callMetrics.handlingTime,
        value: currentAvgHandlingTime,
        percentage: Math.round(handlingTimePercent),
        classname: getCountClassname(handlingTimePercent),
      },
      waitTime: {
        ...callMetrics.waitTime,
        value: currentAvgWaitTime,
        percentage: Math.round(waitTimePercent),
        classname: getCountClassname(waitTimePercent),
      },
      csat: {
        ...callMetrics.csat,
        value: isNaN(positivePercent) ? 0 : Math.round(positivePercent),
        percentage: Math.round(csatChangePercent),
        classname: getCountClassname(csatChangePercent),
        neutral: isNaN(neutralPercent) ? 0 : Math.round(neutralPercent),
        negative: isNaN(negativePercent) ? 0 : Math.round(negativePercent),
      },
    })

    const timeSlots = Array.from({ length: 14 }, (_, i) => {
      const hour = (i + 6) % 12 || 12 // Maps to 6-11, 12, 1-7
      const period = i + 6 < 12 ? 'a' : 'p'
      const timeKey = `${hour}${period}`
      return { x: timeKey, y: 0, color: 'var(--blue-500)' }
    })

    currentData.forEach(({ start_date_time, duration }) => {
      const start = moment(start_date_time)
      const minutes = Math.floor(duration / 60)
      const seconds = duration % 60
      const end = start.clone().add(minutes, 'minutes').add(seconds, 'seconds')

      for (
        let time = start.clone();
        time.isSameOrBefore(end);
        time.add(1, 'hour')
      ) {
        const hour = time.hour() % 12 || 12
        const period = time.hour() < 12 ? 'a' : 'p'
        const timeKey = `${hour}${period}`

        const slot = timeSlots.find((item) => item.x === timeKey)
        if (slot) {
          slot.y += 1

          if (timeKey === currentTime) {
            slot.color = 'var(--orange-400)'
          }
        }
      }
    })

    setMostBusyData(timeSlots)
  }, [callsState, filterIndex, currentTime])

  const onConfirmDeleteCall = async (): Promise<void> => {
    if (selectedCaller?.call_id) {
      try {
        await dispatch(deleteCall(selectedCaller.call_id)).unwrap()
        showToast(createSuccessToast('Success', 'Call deleted successfully.'))
      } catch (err) {
        const message =
          typeof err === 'string'
            ? err
            : err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'Deleting call failed. Please try again.'
        showToast(createFailedToast('Error', message))
      }
    }
  }

  const onDeleteModalHide = (): void => {
    setShowDeleteModal(false)
  }

  const handleCloseAddContactModal = (): void => {
    setShowAddContactModal(false)
    setSelectedContact(null)
  }

  const makeCall = async (_contact: string): Promise<void> => {
    if (_contact) {
      dispatch(SET_INPUT_NUMBER(_contact))
    }
    dispatch(setActiveSidePanel('phone'))
  }

  const getGridCurrentPage = (grid: Grid): number =>
    (grid as unknown as { pageSettings?: { currentPage?: number } })
      .pageSettings?.currentPage ?? 1

  const toggleDetail = (pageRowIndex: number, callId?: string): boolean => {
    const grid = gridRef.current as unknown as Grid | null
    if (!grid?.detailRowModule) return false

    const row = getGridRowIfBound(grid, pageRowIndex)
    if (!row) return false

    const icon = row.querySelector('.expand-btn')
    const detailToggleCell = row.querySelector(
      '.e-detailrowexpand, .e-detailrowcollapse',
    ) as HTMLElement | null
    const isExpanded = expandedRowIndexRef.current === pageRowIndex

    isDetailToggleInProgressRef.current = true
    try {
      if (isExpanded) {
        if (!detailToggleCell) return false
        safeDetailCollapse(grid, detailToggleCell)
        expandedRowIndexRef.current = null
        if (icon) {
          icon.classList.remove('mgc_down_small_fill')
          icon.classList.add('mgc_right_small_fill')
        }
        row.classList.remove('e-row-expanded')
        if (!callId || expandedCallIdRef.current === callId) {
          expandedCallIdRef.current = null
        }
        return true
      }

      collapseGridDetailRow(grid, expandedRowIndexRef.current)
      if (!detailToggleCell) return false

      expandedRowIndexRef.current = pageRowIndex
      if (callId) {
        expandedCallIdRef.current = callId
      }

      if (!safeDetailExpand(grid, detailToggleCell)) {
        if (callId) pendingExpandCallIdRef.current = callId
        return false
      }

      if (icon) {
        icon.classList.remove('mgc_right_small_fill')
        icon.classList.add('mgc_down_small_fill')
      }
      row.classList.add('e-row-expanded')
      return true
    } finally {
      isDetailToggleInProgressRef.current = false
    }
  }

  const expandCallByPageIndex = useCallback(
    (pageRowIndex: number, callId: string): boolean => {
      const grid = gridRef.current as unknown as Grid | null
      if (!grid?.detailRowModule) return false
      if (isDetailToggleInProgressRef.current) return false

      const row = getGridRowIfBound(grid, pageRowIndex)
      if (!row) return false

      if (
        expandedCallIdRef.current === callId &&
        row.querySelector('.e-detailrowcollapse')
      ) {
        return true
      }

      const icon = row.querySelector('.expand-btn')
      const detailToggleCell = row.querySelector(
        '.e-detailrowexpand, .e-detailrowcollapse',
      ) as HTMLElement | null
      if (!detailToggleCell) return false

      isDetailToggleInProgressRef.current = true
      try {
        if (expandedRowIndexRef.current !== pageRowIndex) {
          collapseGridDetailRow(grid, expandedRowIndexRef.current)
        }
        expandedRowIndexRef.current = pageRowIndex
        expandedCallIdRef.current = callId

        if (!safeDetailExpand(grid, detailToggleCell)) return false

        if (icon) {
          icon.classList.remove('mgc_right_small_fill')
          icon.classList.add('mgc_down_small_fill')
        }
        row.classList.add('e-row-expanded')
        return true
      } finally {
        isDetailToggleInProgressRef.current = false
      }
    },
    [],
  )

  const expandCallById = useCallback(
    (callId: string): boolean => {
      const absoluteIndex = filteredCallLogs.findIndex(
        (call) => call.call_id === callId,
      )
      if (absoluteIndex === -1) return false

      const grid = gridRef.current as unknown as Grid | null
      if (!grid?.detailRowModule) {
        pendingExpandCallIdRef.current = callId
        return false
      }

      const targetPage = Math.floor(absoluteIndex / pageSize) + 1
      const pageRelativeIndex = absoluteIndex % pageSize
      const currentPage = getGridCurrentPage(grid)

      if (currentPage !== targetPage) {
        pendingExpandCallIdRef.current = callId
        grid.goToPage(targetPage)
        return false
      }

      const expanded = expandCallByPageIndex(pageRelativeIndex, callId)
      if (expanded) {
        pendingExpandCallIdRef.current = null
      } else {
        pendingExpandCallIdRef.current = callId
      }
      return expanded
    },
    [expandCallByPageIndex, filteredCallLogs, pageSize],
  )

  const retryPendingExpand = useCallback((): boolean => {
    if (isDetailToggleInProgressRef.current) return false
    const callId = pendingExpandCallIdRef.current
    if (!callId) return false

    const expanded = expandCallById(callId)
    if (expanded) {
      pendingExpandCallIdRef.current = null
    }
    return expanded
  }, [expandCallById])

  const toggleCallById = useCallback(
    (callId: string): boolean => {
      const absoluteIndex = filteredCallLogs.findIndex(
        (call) => call.call_id === callId,
      )
      if (absoluteIndex === -1) return false

      const grid = gridRef.current as unknown as Grid | null
      if (!grid?.detailRowModule) {
        pendingExpandCallIdRef.current = callId
        return false
      }

      const targetPage = Math.floor(absoluteIndex / pageSize) + 1
      const pageRelativeIndex = absoluteIndex % pageSize
      const currentPage = getGridCurrentPage(grid)

      if (currentPage !== targetPage) {
        pendingExpandCallIdRef.current = callId
        grid.goToPage(targetPage)
        return false
      }

      if (expandedCallIdRef.current === callId) {
        pendingExpandCallIdRef.current = null
        return toggleDetail(pageRelativeIndex, callId)
      }

      pendingExpandCallIdRef.current = null
      return expandCallByPageIndex(pageRelativeIndex, callId)
    },
    [expandCallByPageIndex, filteredCallLogs, pageSize],
  )

  const reexpandExpandedCall = useCallback((): boolean => {
    if (isDetailToggleInProgressRef.current) return false
    const callId = expandedCallIdRef.current
    if (!callId || pendingExpandCallIdRef.current) return false
    return expandCallById(callId)
  }, [expandCallById])

  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    dispatch(setSearchQuery(event.target.value))
  }

  const handleFilterChange = (selectedFilters: FilterSelection): void => {
    dispatch(setFilters({ ...filters, ...selectedFilters }))
  }

  const handleDropdownChange = (args: string): void => {
    const index = callsDropdownOptions.findIndex((filter) => filter === args)
    dispatch(setDurationIndex(index))
  }

  useEffect(() => {
    if (!filteredAppointmentData) {
      filterAppointmentByDay()
    }
  }, [dayIndex, filteredAppointmentData, filterAppointmentByDay])

  useEffect(() => {
    const details = filterAppointmentByDay()
    setFilteredAppointmentData(details.returnData)
    setMostBusyMaxCount(details.maxCount)
  }, [appointmentData, filterAppointmentByDay])

  useEffect(() => {
    let _calls = callsState

    _calls = _calls.filter((call) => {
      if (filters.status.length !== 0) {
        return call.tags
          ? call.tags.some((t) => filters.status.includes(t))
          : false
      } else return true
    })

    _calls = _calls.filter((call) => {
      if (filters.sentiment.length !== 0) {
        return call.sentiments
          ? filters.sentiment.includes(call.sentiments)
          : false
      } else return true
    })

    _calls = _calls.filter((call) => {
      if (filters.type.length !== 0) {
        return filters.type.includes(getCallDisplayType(call))
      } else return true
    })

    _calls = _calls.filter((call) => {
      if (searchQuery.length !== 0) {
        return call.contact
          ? getFullName(call.contact)
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
              call.contact?.cellphone
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
          : 'unknown caller'.includes(searchQuery.toLowerCase())
      } else return true
    })

    if (filters.aiReceptionist?.length) {
      _calls = _calls.filter((call) => isAiAssistedCall(call, aiActorId))
    }

    setFilteredCallLogs(
      _calls.map((call) => ({
        ...call,
        call_display_type:
          getCallDisplayType(call) === 'Decline'
            ? 'Declined'
            : getCallDisplayType(call),
      })),
    )
  }, [searchQuery, filters, callsState, aiActorId])

  useEffect(() => {
    getPeakHourPerDay()
  }, [mostBusyData, getPeakHourPerDay])

  useEffect(() => {
    getCallCounts()
  }, [callsState, getCallCounts])

  return {
    actionItems,
    menuAnchor,
    addContactItem,
    averageDuration,
    callCount,
    callLogs: callsState,
    callMetrics,
    dayDropdownOptions,
    dayIndex,
    downloadRecordingItem,
    durationIndex,
    filteredCallLogs,
    filters,
    gap,
    gridRef,
    handleShowAISummaryModal,
    handleCloseAddContactModal,
    handleDayOptionChange,
    handleClosePatientInfoModal,
    handleDropdownChange,
    handleFilterChange,
    handleNextDay,
    handleOnClickDropDown,
    handlePreviousDay,
    handleSearch,
    handleAddNote,
    handleViewNotes,
    handleSelect,
    handleSelectTag,
    handleSendMessage,
    handlePageChange,
    generatePresignedUrlForCall,
    makeCall,
    mostBusyData,
    mostBusyMaxCount,
    onConfirmDeleteCall,
    onDeleteModalHide,
    peakHour,
    searchOption,
    searchQuery,
    selectedCaller,
    sentimentDetails,
    setMenuAnchor,
    setAppointmentItem,
    setSelectedCaller,
    setShowAddContactModal,
    setShowAISummaryModal,
    setShowAudioTranscriptModal,
    setShowCallerDetails,
    setShowDeleteModal,
    setSelectedContact,
    showAISummaryModal,
    showAudioTranscriptModal,
    showAddContactModal,
    showDeleteModal,
    showCallerDetails,
    tagItems,
    tagVisibility,
    toggleDetail,
    expandCallById,
    toggleCallById,
    retryPendingExpand,
    reexpandExpandedCall,
    callsLoading,
    callsGridPageSize: CALLS_GRID_PAGE_SIZE,
  }
}

export default useCalls
